import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ApplicationStateService } from '../../../../services/application-state.service';
import { PageService } from '../../../../services/page.service';
import { PageExportService } from '../../../../services/page-export.service';
import { Subscription } from 'rxjs';
import { CommunicationService } from '../../../../services/communication.service';
import { AssetService } from '../../../../services/asset.service';
import { BookFeatureActivationService } from '../../../../services/book-feature-activation.service';
import { ClipboardService } from 'ngx-clipboard';
import { DisabledFeatureModalable } from '../../../../models/disabled-feature-modalable';
import { ModalService } from '../../../../services/modal.service';
import { PlatformLinkService } from '../../../../services/platform-link.service';
import {
    PlatformLinkCopiedConfirmationModalComponent
// eslint-disable-next-line max-len
} from '../../../bookviewer/pages/modal-container/platform-link-copied-confirmation-modal/platform-link-copied-confirmation-modal.component';
import { SharingOptionsComponent } from '../../../utils/sharing-options/sharing-options.component';
import { RealPageNumberPipe } from '../../../../pipes/real-page-number.pipe';
import { AssetPipe } from '../../../../pipes/asset.pipe';

@Component({
    selector: 'app-tools-bar-drop-up',
    templateUrl: './tools-bar-drop-up.component.html',
    styleUrls: ['./tools-bar-drop-up.component.scss'],
    standalone: true,
    imports: [
        SharingOptionsComponent,
        RealPageNumberPipe,
        AssetPipe
    ]
})
export class ToolsBarDropUpComponent implements OnInit, OnDestroy {

    @Input() isMobileVersion: boolean;
    pagesShown: number;

    currentDownloadLink: string;
    doublePageDownloadLink: string;
    leftPageDownloadLink: string;
    rightPageDownloadLink: string;

    private subscription: Subscription;

    constructor(public applicationStateService: ApplicationStateService,
                private pageExportService: PageExportService,
                public featureActivationService: BookFeatureActivationService,
                private communicationService: CommunicationService,
                public pageService: PageService,
                private modalService: ModalService,
                private clipboardService: ClipboardService,
                public assetService: AssetService,
                private platformLinkService: PlatformLinkService) {

        this.subscription = this.communicationService.pageTurnAnnounced$.subscribe(() => {
            this.getDownloadLinks();
        });

        this.communicationService.pageTurnConfirmed$.subscribe(() => {

            if (this.applicationStateService.isScrollMode()) {
                this.getDownloadLinks();
            }
            this.amountPagesShown();
        });
    }

    ngOnInit(): void {
        this.getDownloadLinks();
        this.amountPagesShown();
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /**
     * Returns the real page number of a given page index. If no index is provided to the function the current
     * page index of the ApplicationStateService is taken.
     * @param {number} index
     * @returns {any}
     */
    getRealPageNumber(index?: number): any {

        let realPageNumber;
        if (index) {
            realPageNumber = this.pageService.getRealPageNumber(index);
        } else {
            realPageNumber = this.pageService.getRealPageNumber(this.applicationStateService.currentIndex);
        }

        if (realPageNumber > 0) {
            return realPageNumber;
        }

        return '';
    }

    /**
     * allow to copy left page link if real page number exists
     *
     * @returns {boolean}
     */
    allowLeftCopy(): boolean {
        const page = this.getRealPageNumber(this.applicationStateService.currentIndex);

        return page !== undefined && page > 0;
    }

    /**
     * allow to copy right page link if real page number exists
     *
     * @returns {boolean}
     */
    allowRightCopy(): boolean {
        const page = this.getRealPageNumber(this.applicationStateService.currentIndex + 1);

        return page !== undefined && page > 0;
    }

    /**
     * Copies the link to the page into the clipboard.
     * @param {number} index
     */
    copyLink(index: number): string {

        const realPageNumber = this.pageService.getRealPageNumber(index);
        let currentPath = window.location.href;

        if (realPageNumber <= 0) {
            this.clipboardService.copyFromContent(currentPath);
            this.showCopyHud();

            return currentPath;
        }

        const startOfParams = currentPath.indexOf('?', 0);

        if (startOfParams >= 1) {
            currentPath = currentPath.substring(0, startOfParams);
        }

        const link = `${currentPath}?p=${realPageNumber}`;
        this.clipboardService.copyFromContent(link);
        this.showCopyHud();

        return link;
    }

    onDownloadLinkClicked($event: MouseEvent, link: string): void {
        $event.preventDefault();

        if (!this.featureActivationService.exportPagesActivated) {

            const disabledFeatureModal = new DisabledFeatureModalable();
            disabledFeatureModal.type = ModalService.EXPORT_PAGES;
            disabledFeatureModal.iconUrl = 'draw-mode/icon-tool-notes.png';
            this.modalService.openModal(disabledFeatureModal);

            return;
        }

        window.window.open(link);
    }

    onPlatformLinkCopied($event: {
        platformName: string;
        logoURL: string;
        heading: string;
        description: string
    }): void {
        if ($event) {
            this.platformLinkService.setPlatformLink($event);
            this.modalService.openModal(PlatformLinkCopiedConfirmationModalComponent);
        }
    }

    /**
     * Gets the amount of shown pages.
     * ATTENTION: Although it is marked as unsused method this method is used in the template.
     * @returns {number}
     */
    private amountPagesShown(): number {
        this.pagesShown = this.pageService.getAmountOfCurrentPagesShown();

        return this.pagesShown;
    }

    /**
     * Gets all Download link which are shown in the menu.
     */
    private getDownloadLinks(): void {
        this.pageExportService.getCurrentPagesDownloadLink()
            .then(link => {
                this.currentDownloadLink = link;
            });

        this.pageExportService.getCurrentDoublePageDownloadLink()
            .then(link => {
                this.doublePageDownloadLink = link;
            });

        this.pageExportService.getCurrentLeftPageDownloadLink()
            .then(link => {
                this.leftPageDownloadLink = link;
            });

        this.pageExportService.getCurrentRightPageDownloadLink()
            .then(link => {
                this.rightPageDownloadLink = link;
            });
    }

    /**
     * Shortly fades in a Copy-HUD.
     */
    private showCopyHud(): void {
        const copyElement = $('.copy-hud');
        copyElement.fadeIn('slow', () => {
            setTimeout(() => {
                copyElement.fadeOut('slow');
            }, 2000);
        });
    }
}
